/**
 * Legal documents, English version.
 *
 * Traduction du francais, qui reste la version de reference : en cas de
 * divergence, c'est le texte francais qui fait foi, et la clause de droit
 * applicable le rappelle.
 */
import type { LegalCatalog } from './types.js';

const SIREN = '903 434 702';
const EDITOR = 'Enki Corbin, sole trader (French micro-entreprise)';
const CITY = 'Rennes, France';
const CONTACT = 'contact@cordev.fr';

export const EN: LegalCatalog = {
  legal: {
    title: 'Legal notice',
    updated: 'In force since 10 August 2026',
    sections: [
      {
        heading: 'Publisher',
        blocks: [
          `retusch.io is published by ${EDITOR}, registered under number ${SIREN}, based in ${CITY}.`,
          `Publication director: Enki Corbin. Contact: ${CONTACT}.`,
          'No phone number is provided. Any request, however urgent, should go through the email address above, which is answered within a reasonable time.',
        ],
      },
      {
        heading: 'Hosting',
        blocks: [
          'The site and its files are hosted by Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, United States.',
          'The game server, which runs live games, is hosted by Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Germany.',
        ],
      },
      {
        heading: 'Intellectual property',
        blocks: [
          'The Retusch name, the game code, its interface and the artwork it uses are protected. Reproducing or reusing them, in whole or in part, without prior written permission is prohibited.',
          'Retouches drawn by players remain the property of their author. They are neither kept nor exploited once the game ends.',
        ],
      },
      {
        heading: 'Reporting',
        blocks: [
          `Any content that appears unlawful can be reported to ${CONTACT}. A report is most useful with the date, time and room code, the only details that make it possible to place the facts.`,
          'Since nothing exchanged during a game is kept, a report made after the game has ended cannot be checked against anything.',
        ],
      },
    ],
  },

  privacy: {
    title: 'Privacy',
    updated: 'In force since 10 August 2026',
    sections: [
      {
        heading: 'In short',
        blocks: [
          'Retusch is played without signing up. Everything exchanged during a game lives in the server memory and disappears when the room closes. An account is optional: it keeps your nickname and avatar, and unlocks the paid modes. If you create one, your email address is kept, and nothing else.',
          'The game is free and funded by advertising. That is the only thing setting cookies, and it loads only once you have agreed.',
          'This page sets out precisely what is processed, why, and for how long.',
        ],
      },
      {
        heading: 'Data controller',
        blocks: [
          `${EDITOR}, based in ${CITY}. For any question about your data: ${CONTACT}.`,
        ],
      },
      {
        heading: 'What is processed during a game',
        blocks: [
          'The following passes through the server so that the game can work. None of it is written to disk, none of it is kept after the game.',
          {
            list: [
              'The nickname you choose, 2 to 16 characters. It identifies you to the other players in the room. You are free not to use your real name, and we recommend that you do not.',
              'Your retouches, sent as compressed strokes. The server relays them without ever interpreting them.',
              'Your chat messages, relayed to the other players in the room, then forgotten.',
              'A session token, a randomly generated technical identifier that lets you reconnect if your connection drops. It lives for 60 seconds after a disconnection, then disappears.',
            ],
          },
          'Legal basis: performance of the service you request by joining a game.',
        ],
      },
      {
        heading: 'IP address',
        blocks: [
          'Your IP address is necessarily seen by the server in order to establish the connection. It is used in memory for one purpose only: preventing a single connection point from creating more than five rooms per minute, which protects the service from abuse.',
          'It is kept in a rolling 60 second window, in memory only. It is never logged, never written to disk, and never shared.',
          'Legal basis: the legitimate interest in protecting the service against abuse.',
        ],
      },
      {
        heading: 'Technical logs',
        blocks: [
          'The server records operational events: room code, game settings, number of players, duration. These logs contain no IP address, no nickname, and no drawing or message content.',
          'They are used for diagnostics and security, and kept for twelve months at most.',
        ],
      },
      {
        heading: 'Storage in your browser',
        blocks: [
          'The game itself sets no cookies. Three pieces of information are stored locally in your browser, never leave your device, and can be removed at any time by clearing the site data:',
          {
            list: [
              'your nickname and sound preference, so you are not asked again on every visit,',
              'your choice regarding advertising, so the question is not repeated,',
              'your current session token, to allow reconnection,',
              'two random measurement identifiers, described under “Audience measurement”.',
            ],
          },
          'The advertising provider does set its own cookies if you have consented. They fall under its policy, and are described in the next section.',
        ],
      },
      {
        heading: 'Advertising',
        blocks: [
          'The game is free and funded by advertising, provided by Google through its offering for online games.',
          'No advertising script is loaded before you have expressed a choice. If you accept, Google may set cookies and read information on your device in order to select and measure ads, including in a personalised way. If you decline, advertising is still served but becomes non-personalised: it then relies on no profile.',
          'You can change your choice at any time from the consent management window, which you can reopen from the dedicated link in the message shown on your first visit.',
          'Ads appear only between moments of play, never during a drawing, guessing or reveal phase.',
          'Google acts as a separate controller for these operations. The details of the data it processes and the periods it applies fall under its own privacy rules.',
        ],
      },
      {
        heading: 'Audience measurement',
        blocks: [
          'The game measures its own usage, without cookies, without profiling and without selling anything. Two mechanisms, both limited to aggregate statistics.',
          'The first one is internal: the server records how games unfold (game started, round completed, search successful or not and how long it took, game replayed, drop-out and at which phase), along with page openings and which button was clicked. These events are used to tune difficulty and to understand where the game loses players. They contain no IP address, no nickname, no drawing and no message.',
          'Two random identifiers are attached to them, generated by your browser and carrying no information about you: a session identifier, erased when the tab closes, and a visit identifier kept on your device, used solely to know how many people come back to play another day. Neither identifies you, nor follows you across other sites. Clearing the site data removes them.',
          'These events are kept for at most six months, then deleted automatically.',
          'The second mechanism is Cloudflare Web Analytics, which counts visits, pages and referrers. It works without cookies and without any persistent identifier, and builds no profile.',
          'Neither mechanism relies on a tracker within the meaning of the rules applicable to cookies, so neither requires your prior consent. Should that change, this page would be updated before they go live.',
        ],
      },
      {
        heading: 'Account, if you create one',
        blocks: [
          'The account is OPTIONAL. Without one, none of this concerns you: no address is asked for, and the game works in full.',
          'If you create one, the following is kept:',
          {
            list: [
              'your email address, used to identify you and to secure access,',
              'a password, never stored as such but as an irreversible fingerprint (scrypt), unless you sign in through Google,',
              'your nickname and the avatar you picked,',
              'the creation date and the date of your last sign-in.',
            ],
          },
          'Signing in with Google passes us your email address and your name, and nothing else: not your contacts, not your files, not your history. Google acts as a separate controller for that operation.',
          'Address verification goes through Mailjet (Mailgun France SAS), which receives your address in order to deliver that single message. It is not used for marketing.',
          'This data is kept for as long as the account exists, then deleted within thirty days of its removal. No profiling, no resale, no marketing email: the only reason to write to you is to verify your address or to answer a request of yours.',
          'Legal basis: performance of the contract between us from the moment you create an account.',
        ],
      },
      {
        heading: 'Recipients',
        blocks: [
          'Your data is not sold, rented, or passed to third parties for commercial purposes.',
          'It technically passes through our hosts: Hetzner Online GmbH for the game server, in Germany, and Cloudflare, Inc. for site delivery and traffic measurement.',
          'The advertising provider, Google Ireland Limited, receives the data needed to serve ads, under the conditions described in the "Advertising" section and subject to your consent.',
          'As Cloudflare and Google are linked to entities established in the United States, these operations may involve a transfer outside the European Union, covered by the applicable contractual safeguards and adequacy decisions.',
        ],
      },
      {
        heading: 'Retention periods',
        blocks: [
          {
            list: [
              'Nickname, drawings, messages: for the duration of the game, in memory only.',
              'Session token: 60 seconds after a disconnection.',
              'IP address: 60 seconds, in memory only.',
              'Technical logs, containing no personal data: twelve months at most.',
              'Browser local storage: until you clear it.',
              'Advertising cookies, if you accepted them: periods set by Google, listed in its privacy rules.',
            ],
          },
        ],
      },
      {
        heading: 'Your rights',
        blocks: [
          `You have rights of access, rectification, erasure, objection, restriction and portability over your data. You can exercise them at any time by writing to ${CONTACT}.`,
          'In practice, since no data is kept after your game and no account exists, there is usually nothing to extract or erase: leaving the game is enough. We will tell you so if that is the case.',
          'If you believe your rights are not being respected, you may lodge a complaint with the French data protection authority (CNIL), 3 place de Fontenoy, 75007 Paris.',
        ],
      },
      {
        heading: 'Minors',
        blocks: [
          'The game is not intended for children under 15, the age of digital majority in France. Below that age, use requires the authorisation of a holder of parental authority.',
          'No age check is performed, and no data allowing your age to be known is collected. A parent who notices use by a child may write to us; as nothing is kept, stopping use is enough for any data to disappear.',
        ],
      },
      {
        heading: 'Changes',
        blocks: [
          'This policy will be updated before any change affecting how your data is processed, in particular the introduction of advertising, audience measurement, user accounts or a subscription. The date of entry into force at the top of this page is authoritative.',
        ],
      },
    ],
  },

  terms: {
    title: 'Terms of use',
    updated: 'In force since 10 August 2026',
    sections: [
      {
        heading: 'Purpose',
        blocks: [
          `These terms govern the use of the game Retusch, available at retusch.io and published by ${EDITOR}.`,
          'Using the game means accepting these terms. If you do not accept them, do not use the game.',
        ],
      },
      {
        heading: 'Access to the service',
        blocks: [
          'The game is free and is played without signing up. A recent browser and an internet connection are enough. An account is only needed for the paid modes and to pick an avatar.',
          'The service is provided as is, with no guarantee of availability, continuity or freedom from defects. It may be interrupted, changed or discontinued at any time, in particular for maintenance, without notice or compensation.',
        ],
      },
      {
        heading: 'Rules of conduct',
        blocks: [
          'The game brings people together around a nickname, a drawing and a conversation. You alone are responsible for what you publish there.',
          'The following are prohibited, without this list being exhaustive:',
          {
            list: [
              'publishing unlawful, hateful, discriminatory, violent or sexual content, or content undermining the dignity of others, whether through the nickname, the drawing or the chat,',
              'harassing, threatening or pestering another player,',
              'publishing personal data, your own or someone else’s,',
              'posting advertising, promotional content or unsolicited links,',
              'deliberately disrupting the service, circumventing its limits, or automating its use.',
            ],
          },
          'No prior moderation is carried out: content passes directly between players and is not kept. Access to the service may be restricted or cut off, without notice, for anyone breaching these rules.',
        ],
      },
      {
        heading: 'Intellectual property',
        blocks: [
          'The game, its code, its interface and the artwork it uses belong to the publisher. They may not be copied, modified or reused without written permission.',
          'You keep your rights over the retouches you draw. By drawing them, you agree that they are shown to the other players during the game. They are neither kept nor reused by the publisher once the game ends.',
        ],
      },
      {
        heading: 'Advertising',
        blocks: [
          'The game is free and funded by advertising. Ads appear only between moments of play, never during a drawing, guessing or reveal phase.',
          'Blocking ads by technical means is not prohibited, but it deprives the service of its only source of funding.',
        ],
      },
      {
        heading: 'Minors',
        blocks: [
          'The game is not intended for children under 15. Below that age, use requires the authorisation of a holder of parental authority, who is responsible for supervising that use, in particular of the written chat.',
        ],
      },
      {
        heading: 'Liability',
        blocks: [
          'The publisher cannot be held liable for content published by players, nor for damage resulting from an interruption of the service, the loss of a game in progress, or an incompatibility with your equipment.',
          'Nothing in these terms excludes liability that cannot lawfully be excluded.',
        ],
      },
      {
        heading: 'Changes to these terms',
        blocks: [
          'These terms may be amended, in particular when user accounts, a subscription or advertising are added. The applicable version is the one published on this page, whose date of entry into force appears at the top.',
        ],
      },
      {
        heading: 'Governing law',
        blocks: [
          'These terms are governed by French law. The French version is authoritative. Failing an amicable settlement, any dispute falls within the jurisdiction of the French courts.',
          `For any complaint: ${CONTACT}.`,
        ],
      },
    ],
  },
};
